/**
 * MediaPipe-specific types
 */

export interface MediaPipeLandmark {
  x: number;
  y: number;
  z: number;
  visibility?: number;
}

export interface MediaPipePose {
  poseLandmarks: MediaPipeLandmark[];
  poseWorldLandmarks?: MediaPipeLandmark[];
  segmentationMask?: ImageData;
}

export interface MediaPipeResults {
  image: HTMLVideoElement | HTMLImageElement | HTMLCanvasElement;
  poseLandmarks?: MediaPipeLandmark[];
  poseWorldLandmarks?: MediaPipeLandmark[];
  segmentationMask?: ImageData;
}

// MediaPipe Pose landmark indices
export enum PoseLandmark {
  NOSE = 0,
  LEFT_EYE_INNER = 1,
  LEFT_EYE = 2,
  LEFT_EYE_OUTER = 3,
  RIGHT_EYE_INNER = 4,
  RIGHT_EYE = 5,
  RIGHT_EYE_OUTER = 6,
  LEFT_EAR = 7,
  RIGHT_EAR = 8,
  MOUTH_LEFT = 9,
  MOUTH_RIGHT = 10,
  LEFT_SHOULDER = 11,
  RIGHT_SHOULDER = 12,
  LEFT_ELBOW = 13,
  RIGHT_ELBOW = 14,
  LEFT_WRIST = 15,
  RIGHT_WRIST = 16,
  LEFT_PINKY = 17,
  RIGHT_PINKY = 18,
  LEFT_INDEX = 19,
  RIGHT_INDEX = 20,
  LEFT_THUMB = 21,
  RIGHT_THUMB = 22,
  LEFT_HIP = 23,
  RIGHT_HIP = 24,
  LEFT_KNEE = 25,
  RIGHT_KNEE = 26,
  LEFT_ANKLE = 27,
  RIGHT_ANKLE = 28,
  LEFT_HEEL = 29,
  RIGHT_HEEL = 30,
  LEFT_FOOT_INDEX = 31,
  RIGHT_FOOT_INDEX = 32,
}

// Connection pairs for drawing skeleton
export const POSE_CONNECTIONS: Array<[number, number]> = [
  // Face
  [PoseLandmark.LEFT_EYE, PoseLandmark.RIGHT_EYE],
  [PoseLandmark.LEFT_EYE, PoseLandmark.NOSE],
  [PoseLandmark.RIGHT_EYE, PoseLandmark.NOSE],
  [PoseLandmark.LEFT_EAR, PoseLandmark.LEFT_EYE],
  [PoseLandmark.RIGHT_EAR, PoseLandmark.RIGHT_EYE],
  [PoseLandmark.MOUTH_LEFT, PoseLandmark.MOUTH_RIGHT],

  // Torso
  [PoseLandmark.LEFT_SHOULDER, PoseLandmark.RIGHT_SHOULDER],
  [PoseLandmark.LEFT_SHOULDER, PoseLandmark.LEFT_HIP],
  [PoseLandmark.RIGHT_SHOULDER, PoseLandmark.RIGHT_HIP],
  [PoseLandmark.LEFT_HIP, PoseLandmark.RIGHT_HIP],

  // Left arm
  [PoseLandmark.LEFT_SHOULDER, PoseLandmark.LEFT_ELBOW],
  [PoseLandmark.LEFT_ELBOW, PoseLandmark.LEFT_WRIST],
  [PoseLandmark.LEFT_WRIST, PoseLandmark.LEFT_PINKY],
  [PoseLandmark.LEFT_WRIST, PoseLandmark.LEFT_INDEX],
  [PoseLandmark.LEFT_WRIST, PoseLandmark.LEFT_THUMB],
  [PoseLandmark.LEFT_PINKY, PoseLandmark.LEFT_INDEX],

  // Right arm
  [PoseLandmark.RIGHT_SHOULDER, PoseLandmark.RIGHT_ELBOW],
  [PoseLandmark.RIGHT_ELBOW, PoseLandmark.RIGHT_WRIST],
  [PoseLandmark.RIGHT_WRIST, PoseLandmark.RIGHT_PINKY],
  [PoseLandmark.RIGHT_WRIST, PoseLandmark.RIGHT_INDEX],
  [PoseLandmark.RIGHT_WRIST, PoseLandmark.RIGHT_THUMB],
  [PoseLandmark.RIGHT_PINKY, PoseLandmark.RIGHT_INDEX],

  // Left leg
  [PoseLandmark.LEFT_HIP, PoseLandmark.LEFT_KNEE],
  [PoseLandmark.LEFT_KNEE, PoseLandmark.LEFT_ANKLE],
  [PoseLandmark.LEFT_ANKLE, PoseLandmark.LEFT_HEEL],
  [PoseLandmark.LEFT_ANKLE, PoseLandmark.LEFT_FOOT_INDEX],
  [PoseLandmark.LEFT_HEEL, PoseLandmark.LEFT_FOOT_INDEX],

  // Right leg
  [PoseLandmark.RIGHT_HIP, PoseLandmark.RIGHT_KNEE],
  [PoseLandmark.RIGHT_KNEE, PoseLandmark.RIGHT_ANKLE],
  [PoseLandmark.RIGHT_ANKLE, PoseLandmark.RIGHT_HEEL],
  [PoseLandmark.RIGHT_ANKLE, PoseLandmark.RIGHT_FOOT_INDEX],
  [PoseLandmark.RIGHT_HEEL, PoseLandmark.RIGHT_FOOT_INDEX],
];

export const LANDMARK_NAMES: Record<PoseLandmark, string> = {
  [PoseLandmark.NOSE]: 'Nose',
  [PoseLandmark.LEFT_EYE_INNER]: 'Left Eye Inner',
  [PoseLandmark.LEFT_EYE]: 'Left Eye',
  [PoseLandmark.LEFT_EYE_OUTER]: 'Left Eye Outer',
  [PoseLandmark.RIGHT_EYE_INNER]: 'Right Eye Inner',
  [PoseLandmark.RIGHT_EYE]: 'Right Eye',
  [PoseLandmark.RIGHT_EYE_OUTER]: 'Right Eye Outer',
  [PoseLandmark.LEFT_EAR]: 'Left Ear',
  [PoseLandmark.RIGHT_EAR]: 'Right Ear',
  [PoseLandmark.MOUTH_LEFT]: 'Mouth Left',
  [PoseLandmark.MOUTH_RIGHT]: 'Mouth Right',
  [PoseLandmark.LEFT_SHOULDER]: 'Left Shoulder',
  [PoseLandmark.RIGHT_SHOULDER]: 'Right Shoulder',
  [PoseLandmark.LEFT_ELBOW]: 'Left Elbow',
  [PoseLandmark.RIGHT_ELBOW]: 'Right Elbow',
  [PoseLandmark.LEFT_WRIST]: 'Left Wrist',
  [PoseLandmark.RIGHT_WRIST]: 'Right Wrist',
  [PoseLandmark.LEFT_PINKY]: 'Left Pinky',
  [PoseLandmark.RIGHT_PINKY]: 'Right Pinky',
  [PoseLandmark.LEFT_INDEX]: 'Left Index',
  [PoseLandmark.RIGHT_INDEX]: 'Right Index',
  [PoseLandmark.LEFT_THUMB]: 'Left Thumb',
  [PoseLandmark.RIGHT_THUMB]: 'Right Thumb',
  [PoseLandmark.LEFT_HIP]: 'Left Hip',
  [PoseLandmark.RIGHT_HIP]: 'Right Hip',
  [PoseLandmark.LEFT_KNEE]: 'Left Knee',
  [PoseLandmark.RIGHT_KNEE]: 'Right Knee',
  [PoseLandmark.LEFT_ANKLE]: 'Left Ankle',
  [PoseLandmark.RIGHT_ANKLE]: 'Right Ankle',
  [PoseLandmark.LEFT_HEEL]: 'Left Heel',
  [PoseLandmark.RIGHT_HEEL]: 'Right Heel',
  [PoseLandmark.LEFT_FOOT_INDEX]: 'Left Foot Index',
  [PoseLandmark.RIGHT_FOOT_INDEX]: 'Right Foot Index',
};
